import React from 'react';
import PropTypes from 'prop-types';
import { css, cx } from '@linaria/core';
import { COLORS } from '@livecommerce/design-system';

const headlineMarginBottom = 20;

export const titleClassnames = {
  default: css`
    padding: 0;
    margin: 0;
    font-weight: 500;
    color: ${COLORS.light_700};

    &.border {
      border-bottom-width: 2px;
    }
  `,
  h1: css`
    font-size: 44px;
    line-height: 46px;
    margin-bottom: 9px;

    &.border {
      border-bottom-width: 4px;
    }
  `,
  h2: css`
    font-size: 18px;
    line-height: 20px;
    margin-bottom: 5px;
  `,
  h3: css`
    font-size: 16px;
    line-height: 18px;
    margin-bottom: 5px;
  `,
  h4: css`
    font-size: 14px;
    line-height: 16px;
    margin-bottom: 5px;
  `,
  h5: css`
    font-size: 12px;
    line-height: 14px;
    margin-bottom: 3px;
  `,
  h6: css`
    font-size: 10px;
    line-height: 12px;
    margin-bottom: 3px;
    text-transform: uppercase;
  `,
  active: css`
    color: black;
  `,
  border: css`
    display: inline-block;
    border-bottom-width: 2px;
    border-bottom-style: solid;
    padding-bottom: 8px;
    margin-bottom: 12px;
    border-bottom-color: ${COLORS.dark_700};

    &.active {
      border-bottom-color: black;
    }
  `,

  /*
   * Used to align page headlines properly: A <div> or other block element that wraps an h1
   * and/or wraps sibling elements on the top of a page
   * https://www.figma.com/file/4v7T05BkkTojCra1aWPAMv/ReDesign-Dashboard?node-id=61%3A2012
   */
  pageHeadlineMargins: css`

   /* Target spacing title baseline and content is 74 px. Title block stretches far below baseline though...:
    Expressing 74 spacing as height means headline content has room to breathe without affecting distance to content*/
    margin-top: 46px;
    margin-bottom: ${headlineMarginBottom}px;

    @media (max-width: 768px) {
      margin-top: 130px; /* // 70 + navbar heigh (see Figma: https://www.figma.com/file/VFLJJPThiYzprt0ds38W0W/Invitation-flow-(Copy)?node-id=397%3A0 )*/
      /*
        Observing 10, 20 and 40px bottom margin on various screens in Figma
        https://www.figma.com/proto/VFLJJPThiYzprt0ds38W0W/Invitation-flow-(Copy)?node-id=280%3A6629&scaling=min-zoom
        https://www.figma.com/file/4v7T05BkkTojCra1aWPAMv/ReDesign-Dashboard?node-id=314%3A59 )
        standardization makes sense maybe? 73px (above) feels too spread-out for sm/xs in any case
      */
      margin-bottom: 40px;

      & h1 {
        margin-top: -4px;
        /*Adjust vertical-alignment to match buttons*/
      }
    }
  `,
  uppercase: css`
    text-transform: uppercase;
  `,
};

const allTypes = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const Title = ({ type = 'h1', active = true, border, uppercase, children, className, ...attr }) => {
  const Element = type;
  if (!allTypes.includes(type)) {
    throw Error(`${type} is invalid for Title, these are valid ${allTypes}`);
  }

  const titleClassname = cx(
    titleClassnames.default,
    titleClassnames[type],
    active ? titleClassnames.active : null,
    border ? titleClassnames.border : null,
    uppercase ? titleClassnames.uppercase : null,
    className,
  );
  return (
    <Element className={titleClassname} {...attr}>
      {children}
    </Element>
  );
};

Title.propTypes = {
  type: PropTypes.oneOf(allTypes),
  active: PropTypes.bool,
  border: PropTypes.bool,
  uppercase: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default Title;
